import { createContext, useCallback, useContext, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { MessageCircle, Send, Loader2 } from "lucide-react";
import { products, site } from "@/data/site";
import { IDS } from "@/constants/testIds";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const QuoteContext = createContext(null);
export const useQuote = () => useContext(QuoteContext);

const empty = {
  name: "",
  company: "",
  country: "",
  email: "",
  phone: "",
  product_interest: "",
  quantity: "",
  message: "",
};

export function QuoteDialogProvider({ children }) {
  const [openState, setOpenState] = useState(false);
  const [form, setForm] = useState(empty);
  const [submitting, setSubmitting] = useState(false);

  const open = useCallback((preselectProduct) => {
    setForm({ ...empty, product_interest: preselectProduct || "" });
    setOpenState(true);
  }, []);

  const close = useCallback(() => setOpenState(false), []);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const openWhatsapp = () => {
    const text = [
      `Hello Ashrita Overseas,`,
      form.name && `Name: ${form.name}`,
      form.company && `Company: ${form.company}`,
      form.country && `Country: ${form.country}`,
      form.product_interest && `Product: ${form.product_interest}`,
      form.quantity && `Quantity: ${form.quantity}`,
      form.message && `Message: ${form.message}`,
    ]
      .filter(Boolean)
      .join("\n");
    window.open(
      `https://wa.me/${site.contact.whatsapp}?text=${encodeURIComponent(text)}`,
      "_blank"
    );
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.country || !form.product_interest) {
      toast.error("Please fill name, email, country and product.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      toast.success("Enquiry sent! We'll reply within 24 hours.");
      setForm(empty);
      setOpenState(false);
    } catch (err) {
      console.error(err);
      const msg =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Could not send enquiry. Please try WhatsApp.";
      toast.error(typeof msg === "string" ? msg : "Could not send enquiry.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <QuoteContext.Provider value={{ open, close }}>
      {children}

      <Dialog open={openState} onOpenChange={setOpenState}>
        <DialogContent
          data-testid={IDS.quoteDialog}
          className="max-w-2xl p-0 gap-0 overflow-hidden rounded-2xl"
        >
          <div className="bg-forest text-white px-8 py-7">
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl sm:text-3xl">
                Request an Export Quote
              </DialogTitle>
              <DialogDescription className="text-sage/80 mt-1">
                Share a few details and we&apos;ll reply within one business day.
              </DialogDescription>
            </DialogHeader>
          </div>

          <form onSubmit={submit} className="p-8 space-y-4 max-h-[70vh] overflow-y-auto">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="q-name">Full Name *</Label>
                <Input
                  id="q-name"
                  data-testid={IDS.quoteInputName}
                  value={form.name}
                  onChange={(e) => update("name", e.target.value)}
                  placeholder="Jane Cooper"
                  className="mt-1.5"
                  required
                />
              </div>
              <div>
                <Label htmlFor="q-company">Company</Label>
                <Input
                  id="q-company"
                  data-testid={IDS.quoteInputCompany}
                  value={form.company}
                  onChange={(e) => update("company", e.target.value)}
                  placeholder="Acme Trading Co."
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label htmlFor="q-email">Email *</Label>
                <Input
                  id="q-email"
                  type="email"
                  data-testid={IDS.quoteInputEmail}
                  value={form.email}
                  onChange={(e) => update("email", e.target.value)}
                  placeholder="jane@acme.com"
                  className="mt-1.5"
                  required
                />
              </div>
              <div>
                <Label htmlFor="q-country">Country *</Label>
                <Input
                  id="q-country"
                  data-testid={IDS.quoteInputCountry}
                  value={form.country}
                  onChange={(e) => update("country", e.target.value)}
                  placeholder="United States"
                  className="mt-1.5"
                  required
                />
              </div>
              <div>
                <Label htmlFor="q-phone">Phone / WhatsApp</Label>
                <Input
                  id="q-phone"
                  data-testid={IDS.quoteInputPhone}
                  value={form.phone}
                  onChange={(e) => update("phone", e.target.value)}
                  placeholder="+1 555 000 0000"
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label htmlFor="q-product">Product Interest *</Label>
                <select
                  id="q-product"
                  data-testid={IDS.quoteSelectProduct}
                  value={form.product_interest}
                  onChange={(e) => update("product_interest", e.target.value)}
                  className="mt-1.5 flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  required
                >
                  <option value="">Select a product…</option>
                  {products.map((p) => (
                    <option key={p.id} value={p.name}>
                      {p.name}
                    </option>
                  ))}
                  <option value="Multiple / Other">Multiple / Other</option>
                </select>
              </div>
            </div>

            <div>
              <Label htmlFor="q-qty">Estimated Quantity</Label>
              <Input
                id="q-qty"
                data-testid={IDS.quoteInputQuantity}
                value={form.quantity}
                onChange={(e) => update("quantity", e.target.value)}
                placeholder="e.g. 50,000 units / 1x 40 FT container"
                className="mt-1.5"
              />
            </div>

            <div>
              <Label htmlFor="q-msg">Message</Label>
              <Textarea
                id="q-msg"
                data-testid={IDS.quoteInputMessage}
                value={form.message}
                onChange={(e) => update("message", e.target.value)}
                placeholder="Share sizes, printing, delivery timelines…"
                rows={4}
                className="mt-1.5"
              />
            </div>

            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <Button
                type="submit"
                data-testid={IDS.quoteSubmit}
                disabled={submitting}
                className="flex-1 bg-brand hover:bg-forest text-white rounded-full h-12 font-semibold"
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Sending…
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" /> Send Enquiry
                  </>
                )}
              </Button>
              <Button
                type="button"
                variant="outline"
                data-testid={IDS.quoteWhatsapp}
                onClick={openWhatsapp}
                className="flex-1 border-brand text-brand hover:bg-sage rounded-full h-12 font-semibold"
              >
                <MessageCircle className="h-4 w-4 mr-2" /> Send via WhatsApp
              </Button>
            </div>

            <p className="text-xs text-mist text-center">
              By submitting, you agree to be contacted regarding your enquiry.
            </p>
          </form>
        </DialogContent>
      </Dialog>
    </QuoteContext.Provider>
  );
}
